import { useEffect, useMemo, useRef, useState } from "react";
import type { Shipment } from "../types/DB2";

export type LatLngLiteral = { lat: number; lng: number };

export type GeocodeCacheRecord = {
  lat: number;
  lng: number;
  formattedAddress?: string;
  timestamp: number;
};

const MEMORY_CACHE = new Map<string, GeocodeCacheRecord>();
const STORAGE_PREFIX = "cl_geocode:";
const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;

const readFromStorage = (key: string): GeocodeCacheRecord | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeocodeCacheRecord;
    if (Date.now() - parsed.timestamp > ONE_WEEK) {
      window.localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeToStorage = (key: string, record: GeocodeCacheRecord) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + key,
      JSON.stringify({ ...record, timestamp: Date.now() })
    );
  } catch {
    /* ignore quota errors */
  }
};

const geocodeAddressRequest = async (
  address: string,
  signal?: AbortSignal
): Promise<GeocodeCacheRecord | null> => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.warn("[geocodeAddress] missing Google Maps API key");
    return null;
  }

  const cacheKey = address.toLowerCase();

  const memoryHit = MEMORY_CACHE.get(cacheKey);
  if (memoryHit) return memoryHit;

  const storageHit = readFromStorage(cacheKey);
  if (storageHit) {
    MEMORY_CACHE.set(cacheKey, storageHit);
    return storageHit;
  }

  const encodedAddress = encodeURIComponent(address);
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) {
      console.warn("[geocodeAddress] non-ok response", res.status);
      return null;
    }
    const data = (await res.json()) as {
      results: Array<{
        geometry: { location: { lat: number; lng: number } };
        formatted_address?: string;
      }>;
      status: string;
      error_message?: string;
    };
    if (data.status !== "OK" || !data.results?.length) {
      console.warn("[geocodeAddress] failed lookup", {
        address,
        status: data.status,
        error: data.error_message,
      });
      return null;
    }
    const primary = data.results[0];
    const record: GeocodeCacheRecord = {
      lat: primary.geometry.location.lat,
      lng: primary.geometry.location.lng,
      formattedAddress: primary.formatted_address,
      timestamp: Date.now(),
    };
    MEMORY_CACHE.set(cacheKey, record);
    writeToStorage(cacheKey, record);
    return record;
  } catch (err) {
    if (signal?.aborted) return null;
    console.warn("[geocodeAddress] lookup error", err);
    return null;
  }
};

export const getGeoFromShipment = (
  shipment: Shipment
): { lat: number; lng: number } | null => {
  if (shipment.destinationsGeo) {
    const firstEntry = Object.values(shipment.destinationsGeo)[0];
    if (firstEntry && typeof firstEntry.latitude === "number" && typeof firstEntry.longitude === "number") {
      return {
        lat: (firstEntry as any).latitude,
        lng: (firstEntry as any).longitude,
      };
    }
  }

  // Fall back to any order-level dropoff geo
  const snapshots = shipment.ordersSnapshot
    ? Object.values(shipment.ordersSnapshot)
    : [];
  for (const order of snapshots) {
    const includedItems = order?.includedItemsSnapshot
      ? Object.values(order.includedItemsSnapshot)
      : [];
    for (const item of includedItems) {
      const dropoffGeo = item?.dropoffGeo as
        | { latitude: number; longitude: number }
        | undefined;
      if (
        dropoffGeo &&
        typeof dropoffGeo.latitude === "number" &&
        typeof dropoffGeo.longitude === "number"
      ) {
        return {
          lat: dropoffGeo.latitude,
          lng: dropoffGeo.longitude,
        };
      }
    }
  }

  return null;
};

type ShipmentLocationCandidate = {
  shipmentId: string;
  address: string;
};

const extractAddressCandidates = (shipment: Shipment): ShipmentLocationCandidate[] => {
  const candidates = new Set<string>();
  if (shipment.destinations && shipment.destinations.length) {
    shipment.destinations.forEach((d) => {
      if (typeof d === "string" && d.trim()) candidates.add(d.trim());
    });
  }
  if (shipment.destination && shipment.destination.trim()) {
    candidates.add(shipment.destination.trim());
  }

  const snapshots = shipment.ordersSnapshot
    ? Object.values(shipment.ordersSnapshot)
    : [];
  for (const order of snapshots) {
    // Item-level destinations (highest priority)
    if (order?.includedItemsSnapshot) {
      Object.values(order.includedItemsSnapshot).forEach((itemSnapshot) => {
        const dest =
          typeof itemSnapshot?.destination === "string"
            ? itemSnapshot.destination.trim()
            : "";
        if (dest) {
          candidates.add(dest);
        }
      });
    }

    if (order?.destinations && order.destinations.length) {
      order.destinations.forEach((d) => {
        if (typeof d === "string" && d.trim()) candidates.add(d.trim());
      });
    }
  }

  const addresses = Array.from(candidates);
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV && !addresses.length) {
    console.warn("[geocode] no address candidates derived from items/orders", {
      shipmentId: shipment.id,
    });
  }

  return addresses.map((address) => ({
    shipmentId: shipment.id,
    address,
  }));
};

export const getAddressCandidates = (shipment: Shipment): string[] => {
  return extractAddressCandidates(shipment).map((c) => c.address);
};

export const geocodeShipments = async (
  shipments: Shipment[],
  signal?: AbortSignal
): Promise<Record<string, GeocodeCacheRecord>> => {
  const results: Record<string, GeocodeCacheRecord> = {};
  for (const shipment of shipments) {
    if (signal?.aborted) break;
    const existing = getGeoFromShipment(shipment);
    if (existing) {
      results[shipment.id] = {
        lat: existing.lat,
        lng: existing.lng,
        timestamp: Date.now(),
      };
      continue;
    }

    const addresses = getAddressCandidates(shipment);
    for (const address of addresses) {
      const res = await geocodeAddressRequest(address, signal);
      if (res) {
        results[shipment.id] = res;
        break;
      }
    }
  }
  return results;
};

type UseGeocodeOptions = {
  shipments: Shipment[];
};

export const useShipmentGeocodes = ({
  shipments,
}: UseGeocodeOptions): {
  locations: Record<string, GeocodeCacheRecord>;
  pending: boolean;
  errors: string[];
} => {
  const [locations, setLocations] = useState<Record<string, GeocodeCacheRecord>>({});
  const [pending, setPending] = useState(false);
  const errorsRef = useRef<string[]>([]);

  const shipmentsNeedingLookup = useMemo(() => {
    return shipments.filter((shipment) => {
      const geo = getGeoFromShipment(shipment);
      if (geo) return true;
      const addresses = getAddressCandidates(shipment);
      return addresses.length > 0;
    });
  }, [shipments]);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    const run = async () => {
      setPending(true);
      const merged: Record<string, GeocodeCacheRecord> = {};
      for (const shipment of shipmentsNeedingLookup) {
        if (ignore || controller.signal.aborted) break;
        const geo = getGeoFromShipment(shipment);
        if (geo) {
          merged[shipment.id] = {
            lat: geo.lat,
            lng: geo.lng,
            timestamp: Date.now(),
          };
          continue;
        }

        const addresses = getAddressCandidates(shipment);
        let record: GeocodeCacheRecord | null = null;
        for (const address of addresses) {
          record = await geocodeAddressRequest(address, controller.signal);
          if (record) break;
        }

        if (record) {
          merged[shipment.id] = record;
        } else if (addresses.length) {
          errorsRef.current.push(
            `Unable to locate shipment ${shipment.id} (${addresses[0]})`
          );
        }
      }
      if (!ignore) {
        setLocations(merged);
        setPending(false);
      }
    };

    run();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [shipmentsNeedingLookup]);

  return {
    locations,
    pending,
    errors: errorsRef.current,
  };
};
